import { defineStore } from 'pinia';
import jwtDecode from 'jwt-decode';
import {
  Eatfresh_Basic_User_Type_Enum,
  type Eatfresh_User_Account,
} from '@/gql/graphql';
import { setApolloAuthToken } from '@/apollo';
import {
  ApiRequest,
  ApiRequestRaw,
  handleRes,
  useSharedStore,
} from '@/stores/shared_store';
import type {
  AuthenticationResponseJSON,
  RegistrationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/typescript-types';
import * as yup from 'yup';
import { useUserStore } from './user';
import { useResetStore } from './reset';

interface AuthStoreState {
  authToken: string | null;
  email: string | null;
  id: string | null;
  role: Eatfresh_Basic_User_Type_Enum | null;
  requiresPasswordChange: boolean | null;
  initialized: boolean;
}

export type GetPasswordResetTokenUser = {
  id: string;
  email: string;
  has_password: boolean;
  has_biometrics: boolean;
  user_type: Eatfresh_Basic_User_Type_Enum;
};
export type GetPasswordResetTokenResponse = {
  token: string;
  user: GetPasswordResetTokenUser;
};
export type GetAccountActivationTokenResponse = {
  token: string;
  user: GetPasswordResetTokenUser;
};
export type IResetPasswordToken = {
  token: string;
  id: string;
  user_id: string;
  user_email: string;
  expires_at: string;
  created_at: string;
  used: boolean;
};

type DecodedToken = {
  'https://hasura.io/jwt/claims'?: {
    'x-hasura-user-id'?: string;
    'x-hasura-default-role'?: Eatfresh_Basic_User_Type_Enum;
  };
  requiresUserPasswordChange?: boolean;
  email?: string;
};
export const useAuthStore = defineStore({
  id: 'auth',
  state: (): AuthStoreState => ({
    authToken: null,
    email: null,
    id: null,
    role: null,
    requiresPasswordChange: null,
    initialized: false,
  }),
  getters: {
    isAnonymous: (state) =>
      state.role === Eatfresh_Basic_User_Type_Enum.Anonymous,
  },
  actions: {
    setAuthToken(token: string | null) {
      if (token === this.authToken) return;

      this.authToken = token;

      if (!token) {
        this.email = null;
        this.id = null;
        this.role = null;
        this.requiresPasswordChange = null;
        return;
      }
      //role
      const roleFromToken = this.getUserRoleFromToken(token);
      this.role = roleFromToken;
      //id
      const idFromToken = this.getUserIdFromToken(token);

      this.id = idFromToken;
      //email
      //
      try {
        const emailFromToken = this.getEmailFromToken(token);
        this.email = emailFromToken.value;
      } catch (e) {
        this.email = null;
        if (roleFromToken !== Eatfresh_Basic_User_Type_Enum.Anonymous) {
          throw new Error(
            'Property email not found on non-anonymous jwt_token',
          );
        }
      }

      try {
        //requires_password_change
        const requiresPasswordChangeFromToken =
          this.getPasswordChangeRequiredStatusFromToken(token);
        this.requiresPasswordChange = requiresPasswordChangeFromToken.value;
      } catch (e) {
        this.requiresPasswordChange = null;
        if (roleFromToken !== Eatfresh_Basic_User_Type_Enum.Anonymous) {
          throw new Error(
            'Property requires_password_change not found on non-anonymous jwt_token',
          );
        }
      }

      setApolloAuthToken(token ?? '');
    },
    removeAuthToken() {
      this.setAuthToken(null);
    },

    async restoreTokenFromCookie(): Promise<string> {
      const restoreResponse = await ApiRequestRaw<string>({
        url: '/rest/auth/restore-token-from-cookie',
        options: {
          useAuthorization: false,
          retryOnNetworkError: true,
        },
      });
      if (restoreResponse.ok) {
        const handled = await handleRes<string>(restoreResponse, {
          useAuthorization: false,
        });
        return handled.value;
      }
      if (restoreResponse.status !== 400 && restoreResponse.status !== 401) {
        //error not auth related
        throw new Error('Couldnt be restored');
      }
      const refreshResponse = await ApiRequestRaw<string>({
        url: '/rest/auth/token-refresh',
        options: {
          useAuthorization: false,
        },
      });
      const handled = await handleRes<string>(refreshResponse, {
        useAuthorization: false,
      });
      return handled.value;
    },
    async startWebAuthnEnrollmentByToken(token: string) {
      return ApiRequest<{
        options: PublicKeyCredentialCreationOptionsJSON;
      }>({
        url: '/rest/auth/start-biometric-by-token',
        body: { token },
        options: {
          responseType: 'json',
          useAuthorization: false,
        },
      });
    },

    async startWebAuthnRegistration(body: { email: string }) {
      if (!this.id) {
        const response = await ApiRequest<{
          options: PublicKeyCredentialCreationOptionsJSON;
          token: string;
        }>({
          url: '/rest/auth/start-biometric-registration',
          body,
          options: {
            responseType: 'json',
          },
        });
        if (response.value.token) {
          this.setAuthToken(response.value.token);
        }
        return response;
      } else {
        return ApiRequest<{
          options: PublicKeyCredentialCreationOptionsJSON;
        }>({
          url: '/rest/auth/start-set-biometric',
          body,
          options: {
            responseType: 'json',
          },
        });
      }
    },

    async startWebAuthnSet(body: { email: string }) {
      const response = await ApiRequest<{
        options: PublicKeyCredentialCreationOptionsJSON;
      }>({
        url: '/rest/auth/start-set-biometric',
        body,
        options: {
          responseType: 'json',
        },
      });
      return response;
    },
    async finishWebAuthnRegistration(body: {
      email: string;
      id?: string;
      webAuthnResponse: RegistrationResponseJSON;
    }) {
      const result = await ApiRequest<{ token: string }>({
        url: '/rest/auth/finish-biometric-registration',
        body,
      });

      return result;
    },
    async cancelWebAuthnRegistration(body: { email: string; id?: string }) {
      return await ApiRequest({
        url: '/rest/auth/cancel-biometric-registration',
        body,
      });
    },
    async startWebAuthnLogin(email: string) {
      return await ApiRequest<PublicKeyCredentialRequestOptionsJSON>({
        url: '/rest/auth/start-biometric-login',
        body: {
          email,
        },
        options: {
          responseType: 'json',
        },
      });
    },
    async finishWebAuthnLogin(
      email: string,
      user_resp: AuthenticationResponseJSON,
    ) {
      return await ApiRequest<string>({
        url: '/rest/auth/finish-biometric-login',
        body: {
          email: email,
          webAuthnResponse: user_resp,
        },
      });
    },
    safeJwtDestructure<T>(token: string, key: keyof DecodedToken) {
      const decodedToken = jwtDecode<DecodedToken | undefined>(token);
      if (!decodedToken) {
        throw new Error('Error while decoding jwt token');
      }
      if (!decodedToken[key] && decodedToken[key] !== false) {
        throw new Error(`Error while getting ${key} from jwt token`);
      }

      return {
        value: decodedToken[key] as T,
      };
    },
    async logOut() {
      const res = await ApiRequest({
        url: '/rest/auth/logout',
      });

      this.setAuthToken(null);

      const resetStore = useResetStore();
      resetStore.all();

      return res;
    },
    decodeToken(token: string) {
      return jwtDecode<DecodedToken | undefined>(token);
    },
    getPasswordChangeRequiredStatusFromToken(token: string) {
      return this.safeJwtDestructure<boolean>(
        token,
        'requiresUserPasswordChange',
      );
    },
    getEmailFromToken(token: string) {
      return this.safeJwtDestructure<string>(token, 'email');
    },

    getUserRoleFromToken(token: string) {
      const decoded = this.decodeToken(token);

      if (!decoded) {
        throw new Error('Couldnt decode jwt token');
      }
      if (typeof decoded !== 'object') {
        throw new Error('Invalid decoded format');
      }

      const role =
        decoded?.['https://hasura.io/jwt/claims']?.['x-hasura-default-role'];

      if (!role) {
        throw new Error('Couldnt decode property role');
      }

      return role;
    },
    getUserIdFromToken(token: string) {
      const decoded = this.decodeToken(token);

      if (!decoded) {
        throw new Error('Couldnt decode jwt token');
      }
      if (typeof decoded !== 'object') {
        throw new Error('Invalid decoded format');
      }

      const id =
        decoded?.['https://hasura.io/jwt/claims']?.['x-hasura-user-id'];

      if (!id) {
        throw new Error('Couldnt decode property id');
      }

      return id;
    },
    async loginUser({ email, password }: { email: string; password: string }) {
      const res = await ApiRequest<string>({
        url: '/rest/auth/password-login',
        body: {
          email: email,
          password: password,
        },
        options: {
          responseType: 'string',
          useAuthorization: false,
        },
      });

      const idFromToken = this.getUserIdFromToken(res.value); // get id from token

      return {
        data: {
          userId: idFromToken,
          authToken: res.value,
        },
        response: res.response,
      };
    },
    async loginUserBiometric(email: string) {
      const res = await ApiRequest<string>({
        url: '/rest/auth/biometric-login',
        body: {
          email: email,
        },
        options: {
          responseType: 'string',
          useAuthorization: false,
        },
      });

      const idFromToken = this.getUserIdFromToken(res.value); // get id from token

      return {
        data: {
          userId: idFromToken,
          authToken: res.value,
        },
        response: res.response,
      };
    },

    async resetPassword(body: ResetPasswordRequest) {
      return await ApiRequest<string>({
        url: '/rest/password_reset/reset-password',
        body,
        options: {
          useAuthorization: false,
        },
      });
    },
    async confirmCredentialsReset(
      resetToken: string,
      authMethod: {
        newPassword?: string;
        webAuthnResponse?: RegistrationResponseJSON;
      },
    ) {
      return await ApiRequest<string>({
        url: '/rest/password_reset/confirm-credentials-reset',
        body: {
          token: resetToken,
          authMethod,
        },
        options: {
          useAuthorization: false,
        },
      });
    },

    async getPasswordResetToken(token: string) {
      const res = await ApiRequest<GetPasswordResetTokenResponse>({
        url: '/rest/password_reset/get-reset-password-token',
        body: {
          token,
        },
        options: { responseType: 'json' },
      });
      return res;
    },
    async getAccountActivationToken(token: string) {
      const res = await ApiRequest<GetAccountActivationTokenResponse>({
        url: '/rest/password_reset/get-account-activation-token',
        body: {
          token,
        },
        options: { responseType: 'json' },
      });
      return res;
    },
    async activateUserAccountByToken(
      activationToken: string,
      authMethod: {
        newPassword?: string;
        webAuthnResponse?: RegistrationResponseJSON;
      },
    ) {
      return await ApiRequest<string>({
        url: '/rest/auth/activate-user-account-by-token',
        body: {
          token: activationToken,
          authMethod,
        },
        options: { responseType: 'string' },
      });
    },

    async checkValidLoginCredentials({
      email,
      password,
    }: {
      email: string;
      password: string;
    }) {
      const sharedStore = useSharedStore();
      const ApiRequest = sharedStore.ApiRequest;
      return ApiRequest<{ data: Eatfresh_User_Account }>({
        url: '/rest/auth/check-password-login',
        body: {
          email,
          password,
        },
        options: {
          responseType: 'json',
          useAuthorization: false,
        },
      });
    },

    async loginAnonymousUser(id: string) {
      const loginRes = await ApiRequest<string>({
        url: '/rest/auth/anonymous-login',
        body: {
          id,
        },
        options: {
          responseType: 'string',
          useAuthorization: false,
          retryOnNetworkError: true,
        },
      });

      const token = loginRes.value;
      this.setAuthToken(token); // set token
      const idFromToken = this.getUserIdFromToken(token); // get id from token

      return {
        data: {
          userId: idFromToken,
          token,
        },
        response: loginRes.response,
      };
    },

    async createAndLoginAnonymousUser() {
      if (this.id) {
        throw new Error('Already logged in.');
      }

      const userStore = useUserStore();
      const anonymousUser = await userStore.createAnonymousUser();
      const loginResult = this.loginAnonymousUser(
        anonymousUser.value.database.id,
      );
      return loginResult;
    },

    async loginMethodByEmail(email: string) {
      return ApiRequest<{ methods: LoginMethod[] }>({
        method: 'POST',
        url: '/rest/auth/login-method',
        body: { email },
        options: {
          responseType: 'json',
        },
      });
    },
  },
});

export type LoginMethod = 'biometrics' | 'password';

export enum JwtErrorEnum {
  Expired = 'Expire',
  Used = 'Used',
  Unknown = 'Unknown',
}
function errorMapper(error: Error): JwtErrorEnum {
  if (error.message === 'Token was used already') {
    return JwtErrorEnum.Used;
  }
  if (error.message === 'jwt_expired') {
    return JwtErrorEnum.Expired;
  }
  return JwtErrorEnum.Unknown;
}
export type GetTokenRequestReturnType<T> =
  | {
      success: false;
      error: JwtErrorEnum;
    }
  | {
      success: true;
      value: T;
    };
export async function getPasswordResetToken(
  token: string,
): Promise<GetTokenRequestReturnType<GetPasswordResetTokenResponse>> {
  try {
    const authStore = useAuthStore();
    const res = await authStore.getPasswordResetToken(token);
    return {
      success: true,
      value: res.value,
    };
  } catch (e) {
    if (e instanceof Error) {
      return {
        success: false,
        error: errorMapper(e),
      };
    }
    return {
      success: false,
      error: JwtErrorEnum.Unknown,
    };
  }
}
export async function getAccountActivationToken(
  token: string,
): Promise<GetTokenRequestReturnType<GetAccountActivationTokenResponse>> {
  try {
    const authStore = useAuthStore();
    const res = await authStore.getAccountActivationToken(token);
    return {
      success: true,
      value: res.value,
    };
  } catch (e) {
    if (e instanceof Error) {
      return {
        success: false,
        error: errorMapper(e),
      };
    }
    return {
      success: false,
      error: JwtErrorEnum.Unknown,
    };
  }
}

export async function refreshToken() {
  const token = await ApiRequestRaw({
    url: '/rest/auth/token-refresh',
    options: {
      useAuthorization: false,
    },
  });
  const handledRes = await handleRes<string>(token, {
    useAuthorization: false,
  });
  const authStore = useAuthStore();
  await authStore.setAuthToken(handledRes.value);
  return handledRes;
}
export const resetPasswordRequestSchema = yup.object().shape({
  email: yup.string().email().required(),
});

export type ResetPasswordRequest = yup.InferType<
  typeof resetPasswordRequestSchema
>;

export const confirmResetPasswordSchema = yup.object().shape({
  token: yup.string().required(),
  user_id: yup.string().required(),
  newPassword: yup.string().required(),
});

export type ConfirmResetPassword = yup.InferType<
  typeof confirmResetPasswordSchema
>;

export const passwordLoginSchema = yup.object().shape({
  email: yup.string().email().required(),
  password: yup.string().required(),
});

export const emailSchema = yup.string().email().required();

export type PasswordLoginRequest = yup.InferType<typeof passwordLoginSchema>;
